import { IMeateryBox } from '../interfaces/meateryBox';
import { IOrder, IOrderItem } from '../interfaces/order';
import { IProduct } from '../interfaces/product';
import { roundCurrency } from './helper';

/**
 * ---------------------------------------------------------------------------
 * Order refund engine
 * ---------------------------------------------------------------------------
 * Single source of truth for every refund figure shown or sent by the admin.
 *
 * It supports the four product shapes the catalogue can produce:
 *   SIMPLE   - plain product, priced from its `productInfo` variant
 *   MODIFIER - product with modifier groups, priced base + selected options
 *   COMBO    - `isCombo` product, base is 0 and the price is the options only
 *   BOX      - The Meatery Box bundle, priced by the box itself
 *
 * The order payload is not perfectly self-describing (modifier prices may or may
 * not already be folded into `orderItem.price`, and the cart discount may or may
 * not already be applied to the line prices), so every derived figure is
 * reconciled against the order's own invoice totals rather than assumed.
 */

/** Bahraini Dinar is a 3-decimal currency. */
export const CURRENCY_PRECISION = 3;

/** Half of the smallest representable amount — used for money comparisons. */
const TOLERANCE = 0.0005;

export type OrderItemKind = 'SIMPLE' | 'MODIFIER' | 'COMBO' | 'BOX';

/** A normalised modifier selection for display on an order item. */
export interface ResolvedModifier {
    optionId?: string;
    name: string;
    groupName?: string;
    image: string | null;
    price: number;   // unit price
    quantity: number;
    weight?: string;
    total: number;   // line total for this option
}

/** A component of a bundle (Meatery Box) — informational, never refunded on its own. */
export interface RefundLineComponent {
    id: string;
    name: string;
    image: string | null;
    quantity: number;
    weight?: string;
}

/** One refundable order line, with everything needed to price a partial return. */
export interface RefundLine {
    /** Matches `refundInfo.items[].id` and is what the refund payload references. */
    itemId: string;
    /** Primary key of the order item row. */
    orderItemId: string;
    kind: OrderItemKind;
    name: string;
    image: string | null;
    /** Quantity originally ordered. */
    quantity: number;
    /** Product price for one unit, excluding modifiers. */
    unitBasePrice: number;
    /** Modifier value carried by one unit. */
    unitModifierTotal: number;
    /** Effective charged price of one unit (base + modifiers). */
    unitPrice: number;
    /** `unitPrice * quantity`. */
    lineSubtotal: number;
    modifiers: ResolvedModifier[];
    components: RefundLineComponent[];
    /** True when the customer listed this line in the refund request. */
    isRequested: boolean;
    /** Quantity already refunded on a previous accepted refund. */
    refundedQuantity: number;
    /** Quantity that may still be returned. */
    returnableQuantity: number;
}

export interface RefundContext {
    lines: RefundLine[];
    /** Sum of every line subtotal in the order. */
    itemsSubtotal: number;
    /** Cart/coupon discount carried by the order. */
    discountTotal: number;
    /** VAT charged on top of the item value (0 when prices are VAT inclusive). */
    taxTotal: number;
    taxRate: number;
    deliveryFees: number;
    grandTotal: number;
    /** Amount already refunded against this order. */
    alreadyRefunded: number;
    /** `grandTotal - alreadyRefunded`, never below zero. */
    refundableBalance: number;
    /** True when the discount is already reflected in the line prices. */
    discountAppliedToLinePrices: boolean;
    /** Reconciliation notes worth surfacing to the admin. */
    warnings: string[];
}

/** Map of `RefundLine.itemId` -> quantity being returned. */
export type RefundSelection = Record<string, number>;

export interface RefundLineQuote {
    line: RefundLine;
    quantity: number;
    /** Base product value being returned. */
    baseAmount: number;
    /** Modifier value being returned. */
    modifiersAmount: number;
    /** `baseAmount + modifiersAmount`. */
    subtotal: number;
    /** Share of the order discount attributed to this line. */
    discount: number;
    /** Share of the order VAT attributed to this line. */
    tax: number;
    /** `subtotal - discount + tax`. */
    total: number;
}

export interface RefundQuote {
    lines: RefundLineQuote[];
    /** Number of units being returned across all lines. */
    itemCount: number;
    subtotal: number;
    modifiersSubtotal: number;
    discount: number;
    tax: number;
    deliveryFees: number;
    /** Final refundable amount, already clamped to the remaining balance. */
    total: number;
    /** Total before clamping — differs from `total` only when capped. */
    rawTotal: number;
    isFullReturn: boolean;
    alreadyRefunded: number;
    refundableBalance: number;
    /** Balance left after this refund is accepted. */
    remainingBalance: number;
    warnings: string[];
}

/* -------------------------------------------------------------------------- */
/* Modifier resolution                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Find a modifier option's full detail (name/price/image) inside the embedded
 * product's modifierGroups (nested `modifierGroup.options` or flat `options`).
 * Used only as a fallback — enriched order data is preferred.
 */
export const findOptionDetail = (product: any, optionId?: string): any => {
    if (!product || !optionId) return null;
    const groups = product.modifierGroups || [];
    for (const g of groups) {
        const opts = g?.modifierGroup?.options || g?.options || [];
        const found = opts.find((o: any) => o.id === optionId);
        if (found) return found;
    }
    return null;
};

/**
 * Read the chosen modifiers off an order item. The order payload carries an
 * enriched `item.modifiers[]` (groupName / optionName / unitPrice / totalPrice)
 * but no image; the image lives on `extraData.selectedModifiers[].optionImage`,
 * matched by modifierOptionId. Falls back to the embedded product if needed.
 */
export const getItemModifiers = (item: any): ResolvedModifier[] => {
    const raw =
        item?.modifiers ||
        item?.extraData?.selectedModifiers ||
        item?.extraData?.modifiers ||
        item?.itemDetail?.modifiers ||
        [];
    if (!Array.isArray(raw) || raw.length === 0) return [];

    // Build an optionId -> image map from extraData.selectedModifiers (has optionImage).
    const imageMap: Record<string, string> = {};
    const sel = item?.extraData?.selectedModifiers;
    if (Array.isArray(sel)) {
        for (const s of sel) {
            const id = s.modifierOptionId || s.optionId || s.id;
            if (id && s.optionImage) imageMap[id] = s.optionImage;
        }
    }

    return raw.map((m: any) => {
        const optionId = m.modifierOptionId || m.optionId || m.id;
        const d = findOptionDetail(item?.itemDetail?.product, optionId) || {};
        const name = m.optionName || m.nameOverride || m.name || d.nameOverride || d.name || 'Option';
        const groupName = m.groupName || '';
        const image =
            m.optionImage ||
            (optionId ? imageMap[optionId] : '') ||
            m.imageOverride ||
            m.imageUrl ||
            d.imageOverride ||
            d.imageUrl ||
            null;
        const price = Number(m.unitPrice ?? m.priceOverride ?? m.price ?? d.priceOverride ?? d.price ?? 0) || 0;
        const quantity = Number(m.quantity ?? 1) || 1;
        const total = m.totalPrice != null ? Number(m.totalPrice) || 0 : price * quantity;
        const weight = m.weight || d.weight || undefined;
        return { optionId, name, groupName, image, price, quantity, total, weight };
    });
};

/* -------------------------------------------------------------------------- */
/* Product type resolution                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Name and image to show for an order line.
 *
 * `itemDetail` is the joined catalogue record and can come back empty (deleted
 * or unresolvable product, combos built at checkout). The order item itself
 * carries a snapshot of what the customer bought — `itemTitle`/`itemImage` —
 * so fall back to that before showing a placeholder.
 */
export const getOrderItemDisplay = (item: IOrderItem): { title: string; image: string | null } => {
    const product: any = item?.itemDetail?.product;
    return {
        title: product?.title || item?.itemTitle || 'Item',
        image: product?.images?.[0] || item?.itemImage || null,
    };
};

/**
 * Classify an order line. Detection mirrors the Order Items table: a
 * product/combo carrying modifiers also ships with `isBundle = true`, so the
 * shape of the payload — not the flag alone — decides the kind.
 */
export const getOrderItemKind = (item: IOrderItem, modifiers?: ResolvedModifier[]): OrderItemKind => {
    const product: any = item?.itemDetail?.product;
    const resolved = modifiers ?? getItemModifiers(item);
    const hasModifierGroups = (product?.modifierGroups?.length ?? 0) > 0;
    const isModifierProduct = hasModifierGroups || resolved.length > 0;

    // Without `itemDetail` the line-level flag is the only combo signal.
    if (product?.isCombo || item?.isCombo) return 'COMBO';
    if (item?.isBundle && !isModifierProduct && !!(product as IMeateryBox)?.items) return 'BOX';
    if (isModifierProduct) return 'MODIFIER';
    return 'SIMPLE';
};

/** Bundle contents of a Meatery Box, for display under the returned line. */
const getBoxComponents = (item: IOrderItem): RefundLineComponent[] => {
    const box = item?.itemDetail?.product as IMeateryBox | undefined;
    if (!box?.items?.length) return [];
    return box.items.map((boxItem) => ({
        id: boxItem.id,
        name: boxItem.product?.title || 'Item',
        image: boxItem.product?.images?.[0] || null,
        quantity: Number(boxItem.quantity ?? 1) || 1,
        weight: boxItem.product?.productInfo?.[0]?.pieceWeight,
    }));
};

/**
 * Split the charged unit price into base + modifier value.
 *
 * `orderItem.price` is the charged unit price, but depending on the API version
 * it either already includes the selected modifiers or does not. The variant
 * price on `itemDetail` (0 for combos) tells us which reading reconciles, so we
 * pick whichever interpretation lands closest to the stored price.
 */
export const resolveUnitPricing = (
    item: IOrderItem,
    unitModifierTotal: number,
    kind: OrderItemKind,
): { unitBasePrice: number; unitPrice: number; modifiersIncluded: boolean } => {
    const charged = roundCurrency(item?.price ?? 0);

    if (unitModifierTotal <= 0) {
        return { unitBasePrice: charged, unitPrice: charged, modifiersIncluded: true };
    }

    const detail = item?.itemDetail;
    const rawVariant = detail?.discountedPrice ?? detail?.price;
    // Combos have no variant price at all — their base is 0 by definition.
    const variantPrice = kind === 'COMBO'
        ? 0
        : (rawVariant != null && rawVariant !== '' ? roundCurrency(rawVariant) : NaN);

    if (Number.isFinite(variantPrice)) {
        const distanceIfIncluded = Math.abs(charged - (variantPrice + unitModifierTotal));
        const distanceIfExcluded = Math.abs(charged - variantPrice);
        if (distanceIfIncluded <= distanceIfExcluded) {
            return {
                unitBasePrice: roundCurrency(charged - unitModifierTotal),
                unitPrice: charged,
                modifiersIncluded: true,
            };
        }
        return {
            unitBasePrice: charged,
            unitPrice: roundCurrency(charged + unitModifierTotal),
            modifiersIncluded: false,
        };
    }

    // No variant reference: a price at (or near) the modifier total already
    // contains them; anything else is treated as a base still to be topped up.
    if (Math.abs(charged - unitModifierTotal) <= TOLERANCE) {
        return { unitBasePrice: 0, unitPrice: charged, modifiersIncluded: true };
    }
    return {
        unitBasePrice: charged,
        unitPrice: roundCurrency(charged + unitModifierTotal),
        modifiersIncluded: false,
    };
};

/* -------------------------------------------------------------------------- */
/* Previously refunded quantities                                             */
/* -------------------------------------------------------------------------- */

/** Quantity already refunded per line id, from the refund ledger when present. */
const getRefundedQuantities = (order: IOrder): Record<string, number> => {
    const refunded: Record<string, number> = {};
    const add = (id?: string, quantity?: number) => {
        if (!id) return;
        refunded[id] = (refunded[id] || 0) + (Number(quantity ?? 0) || 0);
    };

    const history = order?.refundInfo?.refundHistory;
    if (Array.isArray(history) && history.length) {
        history.forEach((entry) => entry?.items?.forEach((i) => add(i?.id, i?.quantity)));
        return refunded;
    }

    order?.refundInfo?.refundedItems?.forEach((i) => add(i?.id, i?.quantity));
    return refunded;
};

/** Total value already refunded against this order. */
export const getAlreadyRefundedAmount = (order: IOrder): number => {
    const history = order?.refundInfo?.refundHistory;
    if (Array.isArray(history) && history.length) {
        return roundCurrency(history.reduce((sum, entry) => sum + (Number(entry?.amount ?? 0) || 0), 0));
    }
    // Legacy orders only carry the single accepted amount.
    return roundCurrency(order?.refundInfo?.refundAmount ?? 0);
};

/* -------------------------------------------------------------------------- */
/* Context                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Build the priced, product-type aware view of an order that every refund
 * figure is derived from.
 */
export const buildRefundContext = (order: IOrder): RefundContext => {
    const warnings: string[] = [];
    const orderItems = order?.orderItems ?? [];
    const requested = order?.refundInfo?.items ?? [];
    const refundedQuantities = getRefundedQuantities(order);

    const requestedById = new Map<string, { id: string; name: string; quantity?: number }>();
    requested.forEach((r) => r?.id && requestedById.set(r.id, r));

    const lines: RefundLine[] = orderItems.map((item) => {
        const product: any = item?.itemDetail?.product;
        const display = getOrderItemDisplay(item);
        const modifiers = getItemModifiers(item);
        const kind = getOrderItemKind(item, modifiers);
        const unitModifierTotal = roundCurrency(modifiers.reduce((sum, m) => sum + m.total, 0));
        const { unitBasePrice, unitPrice } = resolveUnitPricing(item, unitModifierTotal, kind);
        const quantity = Math.max(0, Number(item?.quantity ?? 0) || 0);

        // The refund request references the catalogue item id; fall back to the
        // order item row id so a differently-keyed payload still matches.
        const requestMatch =
            requestedById.get(item.itemId) ||
            requestedById.get(item.id) ||
            (product?.id ? requestedById.get(product.id) : undefined);

        const refundedQuantity = Math.min(
            quantity,
            refundedQuantities[item.itemId] ?? refundedQuantities[item.id] ?? 0,
        );

        return {
            itemId: item.itemId || item.id,
            orderItemId: item.id,
            kind,
            name: display.title,
            image: display.image,
            quantity,
            unitBasePrice,
            unitModifierTotal,
            unitPrice,
            lineSubtotal: roundCurrency(unitPrice * quantity),
            modifiers,
            components: kind === 'BOX' ? getBoxComponents(item) : [],
            isRequested: !!requestMatch,
            refundedQuantity,
            returnableQuantity: Math.max(0, quantity - refundedQuantity),
        };
    });

    // A refund request that matches nothing (legacy payload, renamed ids) must
    // not lock the admin out — fall back to letting every line be returned.
    if (requested.length > 0 && !lines.some((l) => l.isRequested)) {
        lines.forEach((l) => { l.isRequested = true; });
        warnings.push('The requested items could not be matched to the order lines, so all items are shown.');
    }

    const itemsSubtotal = roundCurrency(lines.reduce((sum, l) => sum + l.lineSubtotal, 0));
    const discountTotal = Math.max(0, roundCurrency(order?.discountedValue ?? 0));
    const deliveryFees = Math.max(0, roundCurrency(order?.deliveryFees ?? 0));
    const grandTotal = Math.max(0, roundCurrency(order?.grandTotal ?? 0));
    const total = roundCurrency(order?.total ?? 0);
    const withTaxAmount = roundCurrency(order?.withTaxAmount ?? 0);
    // VAT-inclusive catalogue prices leave nothing to add on top.
    const taxTotal = withTaxAmount > total ? roundCurrency(withTaxAmount - total) : 0;

    // Is the discount already baked into the line prices? Compare both readings
    // against the item value implied by the invoice.
    const netItemsTarget = roundCurrency(grandTotal - deliveryFees - taxTotal);
    const discountAppliedToLinePrices =
        grandTotal > 0 && discountTotal > 0
            ? Math.abs(itemsSubtotal - netItemsTarget) <= Math.abs(itemsSubtotal - discountTotal - netItemsTarget)
            : false;

    const alreadyRefunded = getAlreadyRefundedAmount(order);
    const refundableBalance = Math.max(0, roundCurrency(grandTotal - alreadyRefunded));

    // Flag a genuine mismatch so nobody trusts a figure the invoice disagrees with.
    if (grandTotal > 0 && itemsSubtotal > 0) {
        const reconciled = discountAppliedToLinePrices ? itemsSubtotal : roundCurrency(itemsSubtotal - discountTotal);
        if (Math.abs(reconciled - netItemsTarget) > 0.01) {
            warnings.push('Line prices do not reconcile exactly with the order invoice; amounts are allocated proportionally.');
        }
    }

    return {
        lines,
        itemsSubtotal,
        discountTotal,
        taxTotal,
        taxRate: Number(order?.taxRate ?? 0) || 0,
        deliveryFees,
        grandTotal,
        alreadyRefunded,
        refundableBalance,
        discountAppliedToLinePrices,
        warnings,
    };
};

/* -------------------------------------------------------------------------- */
/* Quote                                                                       */
/* -------------------------------------------------------------------------- */

/** Clamp a requested return quantity to what the line still allows. */
export const clampReturnQuantity = (line: RefundLine, quantity: number): number => {
    const requested = Math.floor(Number(quantity) || 0);
    return Math.min(Math.max(requested, 0), line.returnableQuantity);
};

/**
 * Price a return. Discount and VAT are allocated to the returned value in
 * proportion to the order's item subtotal; delivery is only refunded when the
 * whole order goes back, matching the existing behaviour.
 *
 * Allocation is cumulative so the per-line figures always add up to the
 * headline total with no rounding drift, and the result can never exceed the
 * remaining refundable balance.
 */
export const quoteRefund = (context: RefundContext, selection: RefundSelection): RefundQuote => {
    const warnings = [...context.warnings];

    const selected = context.lines
        .map((line) => ({ line, quantity: clampReturnQuantity(line, selection[line.itemId] ?? 0) }))
        .filter((entry) => entry.quantity > 0);

    const subtotal = roundCurrency(selected.reduce((sum, e) => sum + e.line.unitPrice * e.quantity, 0));
    const modifiersSubtotal = roundCurrency(
        selected.reduce((sum, e) => sum + e.line.unitModifierTotal * e.quantity, 0),
    );

    // Only discount that is not already reflected in the line prices is removed.
    const discountBase = context.discountAppliedToLinePrices ? 0 : context.discountTotal;
    const share = context.itemsSubtotal > 0 ? subtotal / context.itemsSubtotal : 0;

    const isFullReturn =
        selected.length > 0 &&
        context.lines.every((line) => {
            const returning = clampReturnQuantity(line, selection[line.itemId] ?? 0);
            return line.quantity === 0 || returning + line.refundedQuantity >= line.quantity;
        });

    const lines: RefundLineQuote[] = [];
    let runningSubtotal = 0;
    let allocatedDiscount = 0;
    let allocatedTax = 0;

    selected.forEach((entry) => {
        const lineSubtotal = roundCurrency(entry.line.unitPrice * entry.quantity);
        runningSubtotal = roundCurrency(runningSubtotal + lineSubtotal);

        const cumulativeShare = subtotal > 0 ? runningSubtotal / subtotal : 0;
        const targetDiscount = roundCurrency(discountBase * share * cumulativeShare);
        const targetTax = roundCurrency(context.taxTotal * share * cumulativeShare);

        const discount = roundCurrency(targetDiscount - allocatedDiscount);
        const tax = roundCurrency(targetTax - allocatedTax);
        allocatedDiscount = targetDiscount;
        allocatedTax = targetTax;

        lines.push({
            line: entry.line,
            quantity: entry.quantity,
            baseAmount: roundCurrency(entry.line.unitBasePrice * entry.quantity),
            modifiersAmount: roundCurrency(entry.line.unitModifierTotal * entry.quantity),
            subtotal: lineSubtotal,
            discount,
            tax,
            total: roundCurrency(lineSubtotal - discount + tax),
        });
    });

    const deliveryFees = isFullReturn ? context.deliveryFees : 0;
    const rawTotal = roundCurrency(subtotal - allocatedDiscount + allocatedTax + deliveryFees);

    let total = rawTotal;
    // A complete return of an untouched order must repay exactly what was charged.
    if (isFullReturn && context.alreadyRefunded === 0 && context.grandTotal > 0) {
        total = context.refundableBalance;
    }
    if (total > context.refundableBalance) {
        total = context.refundableBalance;
        warnings.push('The refund was capped at the remaining refundable balance of this order.');
    }
    if (total < 0) total = 0;

    return {
        lines,
        itemCount: selected.reduce((sum, e) => sum + e.quantity, 0),
        subtotal,
        modifiersSubtotal,
        discount: allocatedDiscount,
        tax: allocatedTax,
        deliveryFees,
        total: roundCurrency(total),
        rawTotal,
        isFullReturn,
        alreadyRefunded: context.alreadyRefunded,
        refundableBalance: context.refundableBalance,
        remainingBalance: Math.max(0, roundCurrency(context.refundableBalance - total)),
        warnings,
    };
};

/**
 * Payload for `orders/update-status`. `refundAmount` keeps the exact legacy
 * contract (a numeric string); `refundDetail` is additive traceability that
 * older backends can ignore.
 */
export const buildRefundPayload = (quote: RefundQuote) => ({
    refundAmount: quote.total.toFixed(CURRENCY_PRECISION),
    refundDetail: {
        subtotal: quote.subtotal.toFixed(CURRENCY_PRECISION),
        modifiersSubtotal: quote.modifiersSubtotal.toFixed(CURRENCY_PRECISION),
        discount: quote.discount.toFixed(CURRENCY_PRECISION),
        tax: quote.tax.toFixed(CURRENCY_PRECISION),
        deliveryFees: quote.deliveryFees.toFixed(CURRENCY_PRECISION),
        total: quote.total.toFixed(CURRENCY_PRECISION),
        isFullReturn: quote.isFullReturn,
        items: quote.lines.map((l) => ({
            id: l.line.itemId,
            name: l.line.name,
            type: l.line.kind,
            quantity: l.quantity,
            unitPrice: l.line.unitPrice.toFixed(CURRENCY_PRECISION),
            amount: l.total.toFixed(CURRENCY_PRECISION),
        })),
    },
});

/** Legacy-only payload, used if the API rejects the enriched one. */
export const buildLegacyRefundPayload = (quote: RefundQuote) => ({
    refundAmount: quote.total.toFixed(CURRENCY_PRECISION),
});

/** Badge label for a line's product type. */
export const getOrderItemKindLabel = (kind: OrderItemKind): string => {
    switch (kind) {
        case 'BOX': return 'Meatery Box';
        case 'COMBO': return 'Combo';
        case 'MODIFIER': return 'Customized';
        default: return 'Product';
    }
};

/** Bootstrap badge classes per product type, matching the Order Items table. */
export const getOrderItemKindBadgeClass = (kind: OrderItemKind): string => {
    switch (kind) {
        case 'BOX': return 'bg-primary bg-opacity-10 text-primary';
        case 'COMBO': return 'bg-warning bg-opacity-10 text-warning';
        case 'MODIFIER': return 'bg-info bg-opacity-10 text-info';
        default: return 'bg-secondary bg-opacity-10 text-primary';
    }
};

/** Kept for callers that only need the display product off a line. */
export const getLineProduct = (item: IOrderItem): IProduct | IMeateryBox | undefined =>
    item?.itemDetail?.product;
